import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp } from 'lucide-react';
import { api, type System, type ReportsPayload } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AgentTrend from '../components/reports/AgentTrend';
import CarrierTrend from '../components/reports/CarrierTrend';
import DetailPanel from '../components/reports/DetailPanel';

export type Metric = 'earned' | 'paid';
export type Drill = { kind: 'agent'; id: string } | { kind: 'carrier'; id: string } | null;

export default function Reports() {
  const { sys: sysSlug } = useParams<{ sys: string }>();
  const sys = (sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null) as System | null;

  const [metric, setMetric] = useState<Metric>('earned');
  const [drill, setDrill] = useState<Drill>(null);

  const q = useQuery({
    queryKey: ['reports', sys],
    queryFn: () => api.getReports(sys as System),
    enabled: !!sys,
  });
  const data: ReportsPayload | undefined = q.data;
  const hasData = !!data && data.periods.length > 0;

  if (!sys) return <div className="alert alert-warning"><p>Pick a system.</p></div>;
  if (q.isLoading) return <LoadingSpinner label="Loading reports…" />;
  if (q.isError) return (
    <div className="alert alert-error"><p>{(q.error as Error).message}</p></div>
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
            <TrendingUp size={20} className="inline mr-2" style={{ color: 'var(--color-primary)' }} />
            Reports — {sys}
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            Trend charts across closed pay runs. Click an agent or carrier to drill in.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span style={{ color: 'var(--color-text-muted)' }}>Metric:</span>
          {(['earned', 'paid'] as Metric[]).map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={`btn ${metric === m ? 'btn-primary' : 'btn-secondary'} text-sm py-1 px-3`}
            >
              {m === 'earned' ? 'Earned' : 'Paid'}
            </button>
          ))}
          <span style={{ color: 'var(--color-text-muted)' }} className="text-xs">(agent view)</span>
        </div>
      </div>

      {!hasData ? (
        <div className="card text-center py-12">
          <p style={{ color: 'var(--color-text-muted)' }}>
            No closed pay runs yet for {sys}.
          </p>
        </div>
      ) : (
        <>
          <AgentTrend data={data!} metric={metric} drill={drill} setDrill={setDrill} />
          <CarrierTrend data={data!} drill={drill} setDrill={setDrill} />
          <DetailPanel data={data!} drill={drill} setDrill={setDrill} />
        </>
      )}
    </div>
  );
}
